import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Checkbox, Empty, Tabs, message, Modal as AntModal } from "antd";
import {
  Button,
  Input,
  MinimalPagination,
  Modal,
  Select,
} from "../../../../shared";
import {
  CloseOutlined,
  DeleteOutlined,
  DisconnectOutlined,
  DownOutlined,
  LinkOutlined,
  PushpinOutlined,
  RightOutlined,
  SearchOutlined,
  StarFilled,
  StarOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import ClipPathIcon from "../../../../../assets/icons/ic_clip-path.svg";
import unpinIcon from "../../../../../assets/icons/ic_unpin.svg";

import RebetLoader from "../../../../CommonComponents/RebetLoader/RebetLoader";
import { API_ROUTES } from "../../../../../api/ApiRoutes";
import { getAuthedInstance } from "../../../../../queries/casino-configuration/collections/useCollectionGames";
import {
  type AdminCollection,
  useAdminCollections,
  useAvailableCollections,
  useNestCollection,
  useUnnestCollection,
} from "../../../../../queries/casino-configuration/collections/useAdminCollections";
import { useCollectionDetail } from "../../../../../queries/casino-configuration/collections/useCollectionDetail";
import { useUpdateAdminCollection } from "../../../../../queries/casino-configuration/collections/useSaveAdminCollection";
import {
  type CollectionGame,
  type CollectionGameTag,
  type CollectionGameGroup,
  type AvailableGame,
  useCollectionGames,
  useCollectionGameGroups,
  useCollectionProviderGames,
  useRemoveCollectionGame,
  useMoveCollectionGame,
  useTogglePopularGame,
  useAvailableGames,
  useAddCollectionGames,
  toMoveTargetPosition,
  toVisualListPosition,
  UNPIN_TO_TOP,
  DEFAULT_PAGE_SIZE as GAMES_TAB_PAGE_SIZE,
} from "../../../../../queries/casino-configuration/collections/useCollectionGames";
import {
  useCollectionProviders,
  useAvailableProviders,
  useAddCollectionProviders,
  useRemoveCollectionProvider,
  useMoveCollectionProvider,
  useProviderItems,
  useMoveProviderItem,
  useSetProviderTags,
  type AvailableProvider,
  type CollectionProvider,
  type ProviderItem,
} from "../../../../../queries/casino-configuration/collections/useCollectionProviders";
import { useAdminTags } from "../../../../../queries/casino-configuration/games/useAdminTags";
import { useSetGameTags } from "../../../../../queries/casino-configuration/games/useSetGameTags";
import TagModal from "../../games/pages/TagModal";
import EditParentPickerModal from "./EditParentPickerModal";
import {
  COLLECTION_ICON_ACCEPT,
  COLLECTION_MAX_ICON_BYTES,
  getCollectionDefaultViewOptions,
  getCollectionKindLabel,
  isAcceptedCollectionIcon,
  isSpecialCollection,
} from "../collectionHelpers";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../../../utils/ErrorUtils";
import { formatString } from "../../../../../utils/constants/stringUtils";
import { useIsDevMode } from "../../../../../hooks/useIsDevMode";
import { useCurationDisplayName } from "../../../../../hooks/useCurationDisplayName";
import CurationEntityName from "../../CurationEntityName";

import "../stylesCollectionsConfiguration.css";

/** API contract: -1 signals the backend to pin the item to position 0 (top). */
const PIN_TO_TOP = -1;

type Props = {
  open: boolean;
  collectionCode: string | null;
  onClose: () => void;
  onNavigate?: (code: string) => void;
  canUploadImages?: boolean;
};

function getTagDisplay(
  tags: CollectionGameTag[] | undefined,
  getTagName: (tag: CollectionGameTag) => string,
): { label: string; color: string } | null {
  if (!tags || tags.length === 0) return null;
  const t = tags[tags.length - 1];
  return { label: getTagName(t) || t.tag_id, color: t.color || "#676d82" };
}

/* ------------------------------------------------------------------ */
/*  General tab                                                       */
/* ------------------------------------------------------------------ */
function GeneralTab({
  detail,
  onSaved,
  canUploadImages = true,
}: {
  detail: AdminCollection;
  onSaved: () => void;
  canUploadImages?: boolean;
}) {
  const [name, setName] = useState(detail.name);
  const [defaultView, setDefaultView] = useState(detail.default_view || "popular");
  const [iconUrl, setIconUrl] = useState(detail.icon_url || "");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Parent-collection state — `null` means "top-level" (no parent), a string
  // is the chosen parent's code. Initialised from the detail payload, with
  // `||` (not `??`) so that any falsy BE value (`null`, `undefined`, `""`)
  // collapses to `null` — an empty string from the backend should mean "no
  // parent", not "render an empty pill".
  const [parentCode, setParentCode] = useState<string | null>(
    detail.parent_code || null,
  );
  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  // Animate-icon flag. Defaults to `false` — both for net-new collections
  // (no field on the response) and for legacy collections where the BE
  // hasn't backfilled the column yet. Admins explicitly opt in per
  // collection via the toggle in the General tab.
  const [isAnimated, setIsAnimated] = useState<boolean>(
    Boolean(detail.is_animated),
  );
  const isSpecial = isSpecialCollection(detail.code);

  // Routing the save through `useUpdateAdminCollection` (rather than calling
  // `patchAdminCollection` directly) ensures both the list and the
  // per-collection detail caches get invalidated on success — so the modal
  // and the collections page reflect the new icon / name without a manual
  // page refresh.
  const { mutateAsync: updateCollection, isPending: saving } =
    useUpdateAdminCollection();

  useEffect(() => {
    setName(detail.name);
    setDefaultView(detail.default_view || "popular");
    setIconUrl(detail.icon_url || "");
    setParentCode(detail.parent_code || null);
    setIsAnimated(Boolean(detail.is_animated));
  }, [detail]);

  // The toggle is "on" whenever a parent code is set — keeps the toggle UI
  // and the parent pill in lockstep with a single source of truth.
  const isSubCollection = parentCode !== null;

  const hasChanges = isSpecial
    ? name !== detail.name ||
      iconUrl !== (detail.icon_url || "") ||
      isAnimated !== Boolean(detail.is_animated)
    : name !== detail.name ||
      defaultView !== (detail.default_view || "popular") ||
      iconUrl !== (detail.icon_url || "") ||
      parentCode !== (detail.parent_code || null) ||
      isAnimated !== Boolean(detail.is_animated);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!canUploadImages) {
      message.error(AppStrings.PAM_UPLOADS_PERMISSION_REQUIRED);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (!isAcceptedCollectionIcon(file)) {
      message.error(AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_FILE_TYPE_INVALID);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (file.size > COLLECTION_MAX_ICON_BYTES) {
      message.error(AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_FILE_TOO_LARGE);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const instance = getAuthedInstance();
      const { data: presignedResp } = await instance.post(
        API_ROUTES.ADMIN_UPLOADS_PRESIGNED,
        {
          image_type: "collection_icon",
          identifier: detail.code,
          content_type: file.type,
          existing_url: iconUrl || null,
        },
      );
      const presigned = presignedResp?.data ?? presignedResp;
      const uploadUrl = presigned?.upload_url;
      if (!uploadUrl) {
        throw new Error(AppStrings.CASINO_CONFIG_COLLECTIONS_PRESIGNED_URL_FAILED);
      }
      await fetch(uploadUrl, {
        method: presigned?.http_method || "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      const newUrl = presigned?.cloudfront_url;
      if (newUrl) {
        const cacheBust = `${newUrl}${newUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;
        setIconUrl(cacheBust);
        message.success(AppStrings.CASINO_CONFIG_COLLECTIONS_ICON_UPLOADED);
      }
    } catch (err: unknown) {
      message.error(
        getApiErrorMessage(
          err,
          (err as Error)?.message ||
            AppStrings.CASINO_CONFIG_COLLECTIONS_UPLOAD_FAILED,
        ),
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleClearIcon = () => setIconUrl("");

  const handleSave = async () => {
    try {
      // Strip the cache-bust query string we add after upload (`?t=...`)
      // before persisting — the canonical icon URL is what gets stored.
      const cleanIconUrl = iconUrl ? iconUrl.split("?")[0] : null;
      await updateCollection({
        code: detail.code,
        body: {
          name,
          default_view: isSpecial
            ? detail.default_view || "popular"
            : defaultView,
          icon_url: cleanIconUrl,
          // Send the locally-edited `parentCode` rather than the original
          // `detail.parent_code` so toggling sub-collection on/off and
          // changing the parent both round-trip through the BE. Special
          // collections keep their existing parent — reparenting would
          // orphan the Features/Views placement (CASENG-1066).
          parent_code: isSpecial ? detail.parent_code || null : parentCode,
          is_active: detail.is_active,
          is_animated: isAnimated,
        },
      });
      message.success(AppStrings.CASINO_CONFIG_COLLECTIONS_SAVED);
      onSaved();
    } catch (err: unknown) {
      message.error(
        getApiErrorMessage(
          err,
          (err as Error)?.message ||
            AppStrings.CASINO_CONFIG_COLLECTIONS_SAVE_FAILED,
        ),
      );
    }
  };

  const handleSubToggle = (next: boolean) => {
    if (next) {
      // Open the picker so the admin can choose a parent. We only flip the
      // state to "sub-collection" once they actually pick one — that way an
      // admin who opens the picker by mistake and closes it doesn't end up
      // with a half-configured sub-collection.
      setParentPickerOpen(true);
    } else {
      setParentCode(null);
      // Also close the picker. The AntModal overlay blocks mouse clicks
      // on the toggle, but keyboard nav (Tab + Space) can still reach the
      // underlying <input>. Without this, an admin who Tabs to the toggle
      // while the picker is open and presses Space would clear parentCode
      // but leave an orphaned picker — and a subsequent click on a row
      // would silently re-enable sub-collection against their intent.
      setParentPickerOpen(false);
    }
  };

  const handleParentPicked = (picked: string | null) => {
    // The picker calls this with `null` for "Top-level" (clear parent) or
    // with a code string for an explicit parent. Either way we close here
    // so the picker doesn't have to also know about close ordering.
    setParentCode(picked);
    setParentPickerOpen(false);
  };

  return (
    <div className="cd-general">
      <div className="cd-general-row">
        <div className="cd-general-field">
          <label className="cd-label">{AppStrings.CASINO_CONFIG_COLLECTIONS_CODE_LABEL}</label>
          <Input value={detail.code} disabled className="cd-input" />
          <span className="cd-hint">{AppStrings.CASINO_CONFIG_COLLECTIONS_IMMUTABLE}</span>
        </div>
        <div className="cd-general-field">
          <label className="cd-label">{AppStrings.CASINO_CONFIG_COLLECTIONS_KIND_LABEL}</label>
          <Input
            value={getCollectionKindLabel(detail.kind)}
            disabled
            className="cd-input"
          />
          <span className="cd-hint">{AppStrings.CASINO_CONFIG_COLLECTIONS_IMMUTABLE}</span>
        </div>
      </div>

      <div className="cd-general-field">
        <label className="cd-label">{AppStrings.CASINO_CONFIG_COLLECTIONS_NAME_LABEL} *</label>
        <Input
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          className="cd-input"
        />
      </div>

      {!isSpecial ? (
        <div className="cd-general-field">
          <label className="cd-label">{AppStrings.CASINO_CONFIG_COLLECTIONS_DEFAULT_VIEW_LABEL}</label>
          <Select
            value={defaultView}
            onChange={(val: string) => setDefaultView(val)}
            className="cd-select"
            options={getCollectionDefaultViewOptions()}
          />
          <span className="cd-hint">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_DEFAULT_VIEW_HINT}
          </span>
        </div>
      ) : null}

      <div className="cd-general-field">
        <label className="cd-label">{AppStrings.CASINO_CONFIG_COLLECTIONS_ICON_LABEL}</label>
        <div className="cd-icon-row">
          {iconUrl ? (
            <img className="cd-icon-preview" src={iconUrl} alt="" />
          ) : (
            <div className="cd-icon-placeholder" />
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={COLLECTION_ICON_ACCEPT}
            style={{ display: "none" }}
            data-testid="icon-file-input"
            onChange={handleFileSelect}
          />
          <Button
            iconLeft={<UploadOutlined />}
            loading={uploading}
            disabled={!canUploadImages}
            onClick={() => fileInputRef.current?.click()}
          >
            {AppStrings.CASINO_CONFIG_GAMES_FORM_REPLACE}
          </Button>
          <Button
            iconLeft={<DeleteOutlined />}
            disabled={!canUploadImages}
            onClick={handleClearIcon}
          >
            {AppStrings.CASINO_CONFIG_GAMES_BULK_CLEAR}
          </Button>
        </div>
        <span className="cd-hint">{AppStrings.CASINO_CONFIG_COLLECTIONS_ICON_HINT}</span>
      </div>

      <div className="cd-subcollection-card">
        <div>
          <div className="cd-subcollection-title">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_ANIMATE_ICON_TITLE}
          </div>
          <div className="cd-hint">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_ANIMATE_ICON_HINT}
          </div>
        </div>
        <label className="cd-toggle-switch">
          <input
            type="checkbox"
            checked={isAnimated}
            onChange={(e) => setIsAnimated(e.target.checked)}
            data-testid="animate-icon-toggle"
            aria-label={
              AppStrings.CASINO_CONFIG_COLLECTIONS_ANIMATE_ICON_TITLE
            }
          />
          <span className="cd-toggle-slider" />
        </label>
      </div>

      {!isSpecial ? (
        <div>
          <div className="cd-subcollection-card">
            <div>
              <div className="cd-subcollection-title">
                {AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_COLLECTION_TITLE}
              </div>
              <div className="cd-hint">
                {AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_COLLECTION_HINT}
              </div>
            </div>
            <label className="cd-toggle-switch">
              <input
                type="checkbox"
                checked={isSubCollection}
                onChange={(e) => handleSubToggle(e.target.checked)}
                data-testid="sub-collection-toggle"
                aria-label={
                  AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_COLLECTION_TITLE
                }
              />
              <span className="cd-toggle-slider" />
            </label>
          </div>
          {isSubCollection ? (
            <div
              className="nc-parent-pill"
              data-testid="sub-collection-parent-pill"
              style={{ marginTop: 8 }}
            >
              <span className="nc-parent-pill-label">
                {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_SUB_PARENT_LABEL}{" "}
                <code>{parentCode}</code>
              </span>
              <div className="nc-parent-pill-actions">
                <button
                  type="button"
                  className="nc-parent-change-btn"
                  onClick={() => setParentPickerOpen(true)}
                >
                  {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_SUB_CHANGE}
                  {" \u203a"}
                </button>
                <button
                  type="button"
                  className="nc-parent-clear-btn"
                  aria-label={AppStrings.CASINO_CONFIG_COLLECTIONS_CLEAR_PARENT_ARIA}
                  onClick={() => setParentCode(null)}
                >
                  <CloseOutlined />
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {!isSpecial ? (
        <EditParentPickerModal
          open={parentPickerOpen}
          currentCode={detail.code}
          kind={detail.kind}
          selectedCode={parentCode}
          onSelect={handleParentPicked}
          onClose={() => setParentPickerOpen(false)}
          // Hide Top-level when a parent is already set: clearing should
          // go through the explicit × on the parent pill, not a silent
          // re-pick that just happens to mean "detach". An admin opening
          // the picker via "Change" is browsing for a *different* parent,
          // not looking to clear.
          hideTopLevel={parentCode !== null}
        />
      ) : null}

      <div className="cd-general-footer">
        {hasChanges && (
          <span className="cd-unsaved-label">{AppStrings.CASINO_CONFIG_COLLECTIONS_UNSAVED}</span>
        )}
        <Button
          className={`cd-save-btn ${hasChanges ? "cd-save-btn--active" : ""}`}
          loading={saving}
          onClick={handleSave}
        >
          {hasChanges ? AppStrings.CASINO_CONFIG_COLLECTIONS_SAVE : AppStrings.CASINO_CONFIG_COLLECTIONS_SAVED}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Game row                                                          */
/* ------------------------------------------------------------------ */
function GameRow({
  game,
  idx,
  code,
  view,
  showStar,
  groupedByProvider,
  popularGameKeys,
  onDragStart,
  onDragOver,
  onDrop,
  onDragCancel,
  dragOverIdx,
}: {
  game: CollectionGame;
  idx: number;
  code: string;
  view: "all" | "popular";
  showStar: boolean;
  groupedByProvider?: boolean;
  popularGameKeys?: Set<string>;
  onDragStart?: (idx: number) => void;
  onDragOver?: (e: React.DragEvent, idx: number) => void;
  onDrop?: (idx: number) => void;
  onDragCancel?: () => void;
  dragOverIdx?: number | null;
}) {
  const { mutateAsync: removeGame, isPending: removing } =
    useRemoveCollectionGame(code);
  const { mutateAsync: moveGame, isPending: moving } =
    useMoveCollectionGame(code);
  const { mutateAsync: togglePopular, isPending: togglingPopular } =
    useTogglePopularGame(code);
  const { data: adminTags = [] } = useAdminTags();
  const { mutateAsync: setGameTagsMutation } = useSetGameTags();
  const queryClient = useQueryClient();
  const { getName: getTagName } = useCurationDisplayName();
  const [tagModalOpen, setTagModalOpen] = useState(false);

  const isPinned = Boolean(game.is_pinned);
  const gameKey = `${game.provider}::${game.game_id}`;
  const isPopular = popularGameKeys
    ? popularGameKeys.has(gameKey)
    : Boolean(
        (game as Record<string, unknown>).popular ||
          (game as Record<string, unknown>).is_popular,
      );

  const tagDisplay = getTagDisplay(game.tags, getTagName);

  const handleRemove = () => {
    AntModal.confirm({
      title: formatString(AppStrings.CASINO_CONFIG_COLLECTIONS_REMOVE_GAME_TITLE, {
        name: game.name,
      }),
      content: AppStrings.CASINO_CONFIG_COLLECTIONS_REMOVE_GAME_CONTENT,
      okText: AppStrings.CASINO_CONFIG_COLLECTIONS_REMOVE_OK,
      okType: "danger",
      onOk: async () => {
        try {
          await removeGame({ gameId: game.game_id, provider: game.provider });
          message.success(
            formatString(AppStrings.CASINO_CONFIG_COLLECTIONS_GAME_REMOVED, {
              name: game.name,
            }),
          );
        } catch (err: unknown) {
          message.error(
            getApiErrorMessage(
              err,
              (err as Error)?.message || AppStrings.PAM_GENERIC_REQUEST_FAILED,
            ),
          );
        }
      },
    });
  };

  const handleTogglePopular = async () => {
    try {
      await togglePopular({
        provider: game.provider,
        game_id: game.game_id,
        is_popular: !isPopular,
      });
    } catch (err: unknown) {
      message.error(
        (err as Error)?.message ||
          AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_UPDATE_POPULAR,
      );
    }
  };

  const handlePin = async () => {
    try {
      await moveGame({
        view: groupedByProvider ? "all" : view,
        provider: game.provider,
        game_id: game.game_id,
        target_position: isPinned ? UNPIN_TO_TOP : PIN_TO_TOP,
        groupedByProvider,
      });
    } catch (err: unknown) {
      message.error(
        (err as Error)?.message ||
          AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_PIN_GAME,
      );
    }
  };

  const currentTagIds = (game.tags ?? []).map((t) =>
    typeof t === "string" ? t : t.tag_id,
  );

  const handleSetTag = async (tagIds: string[]) => {
    try {
      await setGameTagsMutation({
        provider: game.provider,
        game_id: game.game_id,
        tag_ids: tagIds,
      });
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections", code, "games"],
      });
      message.success(AppStrings.CASINO_CONFIG_COLLECTIONS_TAG_SET);
    } catch (err: unknown) {
      message.error((err as Error)?.message || AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_SET_TAG);
      throw err;
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    try {
      const instance = getAuthedInstance();
      const url = API_ROUTES.DELETE_GAME_TAG
        .replace("{provider}", encodeURIComponent(game.provider))
        .replace("{game_id}", encodeURIComponent(game.game_id))
        .replace("{tag_id}", encodeURIComponent(tagId));
      await instance.delete(url);
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections", code, "games"],
      });
      message.success(AppStrings.CASINO_CONFIG_COLLECTIONS_TAG_REMOVED);
    } catch (err: unknown) {
      message.error((err as Error)?.message || AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_SET_TAG);
      throw err;
    }
  };

  return (
    <>
      <div
        className={`cd-game-row ${groupedByProvider ? "cd-game-row--nested" : ""} ${dragOverIdx === idx ? "cd-game-row--drag-over" : ""}`}
        draggable
        data-testid={`game-row-${game.provider}-${game.game_id}`}
        onDragStart={() => onDragStart?.(idx)}
        onDragOver={(e) => onDragOver?.(e, idx)}
        onDrop={() => onDrop?.(idx)}
        onDragEnd={() => onDragCancel?.()}
      >
        <img src={ClipPathIcon} alt="" className="cd-game-drag-icon" />
        <span
          className="cd-game-idx"
          data-testid={`game-idx-${game.provider}-${game.game_id}`}
        >
          {toVisualListPosition(idx)}
        </span>
        <div className="cd-game-label">
          <span className="cd-game-name">{game.name}</span>
          <span
            className="cd-game-provider"
            title={`${game.provider}/${game.game_id}`}
          >
            {game.provider}/{game.game_id}
          </span>
        </div>
        {isPinned && (
          <span className="cd-game-pinned-badge">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_BADGE_PINNED}
          </span>
        )}
        {(isPopular || isPinned) && (
          <span className="cd-game-popular-badge">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_BADGE_POPULAR}
          </span>
        )}
        {game.aggregator && (
          <span className="cd-game-agg">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_BADGE_AGG}
          </span>
        )}
        <span
          className={`cd-game-status ${
            game.is_active
              ? "cd-game-status--active"
              : "cd-game-status--inactive"
          }`}
        >
          {game.is_active ? AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_ACTIVE : AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_INACTIVE}
        </span>
        <button
          type="button"
          className="cd-game-tag-btn"
          onClick={() => setTagModalOpen(true)}
        >
          {tagDisplay ? (
            <span>
              <span
                className="cd-game-tag-dot"
                style={{ background: tagDisplay.color }}
              />
              {tagDisplay.label}
            </span>
          ) : (
            AppStrings.CASINO_CONFIG_GAMES_TAG_ADD
          )}
        </button>
        {showStar && (
          <button
            type="button"
            className={`cd-game-star ${isPopular ? "cd-game-star--active" : ""}`}
            onClick={handleTogglePopular}
            disabled={togglingPopular}
            aria-label={formatString(
              AppStrings.CASINO_CONFIG_COLLECTIONS_TOGGLE_POPULAR_ARIA,
              { name: game.name },
            )}
          >
            {isPopular ? <StarFilled /> : <StarOutlined />}
          </button>
        )}
        <button
          type="button"
          className={`cd-game-pin ${isPinned ? "cd-game-pin--active" : ""}`}
          onClick={handlePin}
          disabled={moving}
          aria-label={formatString(
            isPinned
              ? AppStrings.CASINO_CONFIG_COLLECTIONS_UNPIN_ARIA
              : AppStrings.CASINO_CONFIG_COLLECTIONS_PIN_ARIA,
            { name: game.name },
          )}
        >
          {isPinned ? (
            <img
              src={unpinIcon}
              alt={AppStrings.CASINO_CONFIG_COLLECTIONS_UNPIN_ICON_ALT}
              className="cd-pin-img"
            />
          ) : (
            <PushpinOutlined />
          )}
        </button>
        <button
          type="button"
          className="cd-game-remove"
          disabled={removing}
          onClick={handleRemove}
          aria-label={formatString(
            AppStrings.CASINO_CONFIG_COLLECTIONS_REMOVE_ROW_ARIA,
            { name: game.name },
          )}
        >
          <CloseOutlined />
        </button>
      </div>

      <TagModal
        open={tagModalOpen}
        gameLabel={`${game.provider}/${game.game_id}`}
        currentTags={currentTagIds}
        availableTags={adminTags}
        loading={false}
        onSetTag={handleSetTag}
        onRemoveTag={handleRemoveTag}
        onClose={() => setTagModalOpen(false)}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Grouped by provider view                                          */
/* ------------------------------------------------------------------ */
function ExpandedProviderGames({
  code,
  provider,
  search,
}: {
  code: string;
  provider: string;
  search?: string;
}) {
  const { data, isLoading, error } = useCollectionProviderGames(code, provider, search);
  const { mutateAsync: moveGame } = useMoveCollectionGame(code);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const games = data?.items ?? [];

  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIdx(idx);
  };
  const handleDragCancel = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleDrop = async (dropIdx: number) => {
    const game = dragIdx !== null && dragIdx !== dropIdx ? games[dragIdx] : null;
    const target = games[dropIdx];
    setDragIdx(null);
    setDragOverIdx(null);
    if (!game || !target) return;
    try {
      await moveGame({
        view: "all",
        provider: game.provider,
        game_id: game.game_id,
        target_position: toMoveTargetPosition({
          target,
          dropIdx,
          movedIsPinned: Boolean(game.is_pinned),
          items: games,
        }),
        groupedByProvider: true,
      });
    } catch (err: unknown) {
      message.error((err as Error)?.message || AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_MOVE_GAME);
    }
  };

  if (isLoading) {
    return (
      <div className="cd-games-loader">
        <RebetLoader height="32px" width="32px" />
      </div>
    );
  }

  if (error) {
    return (
      <Empty
        description={AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_LOAD_PROVIDER_GAMES}
      />
    );
  }

  return (
    <>
      {games.map((game, gIdx) => (
        <GameRow
          key={`${game.provider}_${game.game_id}`}
          game={game}
          idx={gIdx}
          code={code}
          view="all"
          showStar={false}
          groupedByProvider
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragCancel={handleDragCancel}
          dragOverIdx={dragOverIdx}
        />
      ))}
    </>
  );
}

function ProviderGroupView({ code, search }: { code: string; search?: string }) {
  const { data: groups, isLoading, error } = useCollectionGameGroups(code);
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
  const { mutateAsync: moveGame } = useMoveCollectionGame(code);

  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const groupList: CollectionGameGroup[] = useMemo(
    () => (Array.isArray(groups) ? groups : []),
    [groups],
  );

  const handlePinProvider = async (provider: string, isPinned: boolean) => {
    try {
      await moveGame({
        view: "all",
        provider,
        game_id: "",
        target_position: isPinned ? UNPIN_TO_TOP : PIN_TO_TOP,
        groupedByProvider: true,
      });
    } catch (err: unknown) {
      message.error(
        (err as Error)?.message ||
          AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_PIN_PROVIDER,
      );
    }
  };

  const handleProviderDrop = async (dropIdx: number) => {
    const group = dragIdx !== null && dragIdx !== dropIdx ? groupList[dragIdx] : null;
    const target = groupList[dropIdx];
    setDragIdx(null);
    setDragOverIdx(null);
    if (!group || !target) return;
    const provider = String(group.provider ?? "");
    try {
      await moveGame({
        view: "all",
        provider,
        game_id: "",
        target_position: toMoveTargetPosition({
          target,
          dropIdx,
          movedIsPinned: Boolean(group.is_pinned),
          items: groupList,
        }),
        groupedByProvider: true,
      });
    } catch (err: unknown) {
      message.error(
        (err as Error)?.message ||
          AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_MOVE_PROVIDER,
      );
    }
  };

  if (isLoading) {
    return (
      <div className="cd-games-loader">
        <RebetLoader height="48px" width="48px" />
      </div>
    );
  }

  if (error) {
    return (
      <Empty
        description={AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_LOAD_PROVIDER_GROUPS}
      />
    );
  }

  return (
    <div className="cd-provider-groups">
      {groupList.map((group: CollectionGameGroup, idx: number) => {
        const provider = String(group.provider ?? "");
        const gameCount = Number(group.game_count ?? 0);
        const isPinned = Boolean(group.is_pinned);
        const isExpanded = expandedProvider === provider;

        return (
          <div key={provider || idx}>
            <div
              className={`cd-provider-row ${dragOverIdx === idx ? "cd-game-row--drag-over" : ""}`}
              draggable
              data-testid={`provider-row-${provider}`}
              onDragStart={() => setDragIdx(idx)}
              onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
              onDrop={() => handleProviderDrop(idx)}
              onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
            >
              <img src={ClipPathIcon} alt="" className="cd-game-drag-icon" />
              <span className="cd-game-idx" data-testid={`provider-group-idx-${provider}`}>
                {toVisualListPosition(idx)}
              </span>
              <button
                type="button"
                className="cd-provider-expand"
                aria-label={formatString(
                  AppStrings.CASINO_CONFIG_COLLECTIONS_EXPAND_PROVIDER_ARIA,
                  { name: provider },
                )}
                onClick={() =>
                  setExpandedProvider(isExpanded ? null : provider)
                }
              >
                {isExpanded ? <DownOutlined /> : <RightOutlined />}
              </button>
              <span className="cd-provider-name">
                {provider.toUpperCase()}
              </span>
              {isPinned && (
                <span className="cd-game-pinned-badge">
                  {AppStrings.CASINO_CONFIG_COLLECTIONS_BADGE_PINNED}
                </span>
              )}
              <span className="cd-provider-count">
                {gameCount}{" "}
                {gameCount === 1
                  ? AppStrings.CASINO_CONFIG_COLLECTIONS_GAME_SUFFIX
                  : AppStrings.CASINO_CONFIG_COLLECTIONS_GAMES_SUFFIX}
              </span>
              <button
                type="button"
                className={`cd-game-pin ${isPinned ? "cd-game-pin--active" : ""}`}
                onClick={() => handlePinProvider(provider, isPinned)}
                aria-label={formatString(
                  isPinned
                    ? AppStrings.CASINO_CONFIG_COLLECTIONS_UNPIN_ARIA
                    : AppStrings.CASINO_CONFIG_COLLECTIONS_PIN_ARIA,
                  { name: provider },
                )}
              >
                {isPinned ? (
                  <img
                    src={unpinIcon}
                    alt={AppStrings.CASINO_CONFIG_COLLECTIONS_UNPIN_ICON_ALT}
                    className="cd-pin-img"
                  />
                ) : (
                  <PushpinOutlined />
                )}
              </button>
            </div>
            {isExpanded && (
              <ExpandedProviderGames code={code} provider={provider} search={search} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Provider row (for providers-kind collections)                      */
/* ------------------------------------------------------------------ */
function ProviderRow({
  provider,
  idx,
  code,
  onDragStart,
  onDragOver,
  onDrop,
  onDragCancel,
  dragOverIdx,
}: {
  provider: CollectionProvider;
  idx: number;
  code: string;
  onDragStart?: (i: number) => void;
  onDragOver?: (e: React.DragEvent, i: number) => void;
  onDrop?: (i: number) => void;
  onDragCancel?: () => void;
  dragOverIdx?: number | null;
}) {
  const queryClient = useQueryClient();
  const { mutateAsync: removeProvider, isPending: removing } =
    useRemoveCollectionProvider(code);
  const { mutateAsync: setProviderTags } = useSetProviderTags(code);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [drillOpen, setDrillOpen] = useState(false);
  const { data: adminTags = [] } = useAdminTags();
  const { getName: getTagName } = useCurationDisplayName();

  const lastTag = provider.tags?.length ? provider.tags[provider.tags.length - 1] : null;
  const tagDisplay = lastTag
    ? { label: getTagName(lastTag), color: lastTag.color || "#888" }
    : null;

  const currentTagIds = (provider.tags ?? []).map((t) => t.tag_id);

  const handleSetTag = async (tagIds: string[]) => {
    try {
      await setProviderTags({
        providerId: provider.provider_id,
        tagIds,
      });
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections", code, "providers"],
      });
      message.success(AppStrings.CASINO_CONFIG_COLLECTIONS_TAG_SET);
    } catch (err: unknown) {
      message.error((err as Error)?.message || AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_SET_TAG);
      throw err;
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    try {
      const instance = getAuthedInstance();
      const url = API_ROUTES.DELETE_PROVIDER_TAG
        .replace("{provider_id}", encodeURIComponent(provider.provider_id))
        .replace("{tag_id}", encodeURIComponent(tagId));
      await instance.delete(url);
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections", code, "providers"],
      });
      message.success(AppStrings.CASINO_CONFIG_COLLECTIONS_TAG_REMOVED);
    } catch (err: unknown) {
      message.error((err as Error)?.message || AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_SET_TAG);
      throw err;
    }
  };

  const isPinned = Boolean(provider.is_pinned);

  const { mutateAsync: moveProvider, isPending: moving } =
    useMoveCollectionProvider(code);

  const handlePin = async () => {
    try {
      await moveProvider({
        provider_id: provider.provider_id,
        target_position: isPinned ? UNPIN_TO_TOP : PIN_TO_TOP,
      });
    } catch (err: unknown) {
      message.error(
        (err as Error)?.message ||
          AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_MOVE_PROVIDER,
      );
    }
  };

  const handleRemove = () => {
    AntModal.confirm({
      title: formatString(
        AppStrings.CASINO_CONFIG_COLLECTIONS_REMOVE_PROVIDER_TITLE,
        { name: provider.name },
      ),
      content: AppStrings.CASINO_CONFIG_COLLECTIONS_REMOVE_PROVIDER_CONTENT,
      okText: AppStrings.CASINO_CONFIG_COLLECTIONS_REMOVE_OK,
      okType: "danger",
      onOk: async () => {
        try {
          await removeProvider({
            identifier: provider.identifier ?? provider.provider_id,
          });
          message.success(
            formatString(AppStrings.CASINO_CONFIG_COLLECTIONS_PROVIDER_REMOVED, {
              name: provider.name,
            }),
          );
        } catch (err: unknown) {
          message.error(
            getApiErrorMessage(
              err,
              (err as Error)?.message || AppStrings.PAM_GENERIC_REQUEST_FAILED,
            ),
          );
        }
      },
    });
  };

  return (
    <>
      <div
        className={`cd-game-row ${dragOverIdx === idx ? "cd-game-row--drag-over" : ""}`}
        data-testid={`provider-row-${idx}`}
        draggable
        onDragStart={() => onDragStart?.(idx)}
        onDragOver={(e) => onDragOver?.(e, idx)}
        onDrop={() => onDrop?.(idx)}
        onDragEnd={() => onDragCancel?.()}
      >
        <img src={ClipPathIcon} alt="" className="cd-game-drag-icon" />
        <span className="cd-game-idx" data-testid={`provider-idx-${provider.provider_id}`}>
          {toVisualListPosition(idx)}
        </span>
        <span className="cd-game-name">
          {provider.name}
          <span className="cd-game-identifier"> {provider.provider_id}</span>
        </span>
        {isPinned && (
          <span className="cd-game-pinned-badge">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_BADGE_PINNED}
          </span>
        )}
        <span
          className={`cd-game-status ${
            provider.is_active
              ? "cd-game-status--active"
              : "cd-game-status--inactive"
          }`}
        >
          {provider.is_active ? AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_ACTIVE : AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_INACTIVE}
        </span>
        <button
          type="button"
          className={`cd-game-pin ${isPinned ? "cd-game-pin--active" : ""}`}
          onClick={handlePin}
          disabled={moving}
          aria-label={formatString(
            isPinned
              ? AppStrings.CASINO_CONFIG_COLLECTIONS_UNPIN_ARIA
              : AppStrings.CASINO_CONFIG_COLLECTIONS_PIN_ARIA,
            { name: provider.name },
          )}
        >
          {isPinned ? (
            <img
              src={unpinIcon}
              alt={AppStrings.CASINO_CONFIG_COLLECTIONS_UNPIN_ICON_ALT}
              className="cd-pin-img"
            />
          ) : (
            <PushpinOutlined />
          )}
        </button>
        <button
          type="button"
          className="cd-game-tag-btn"
          onClick={() => setTagModalOpen(true)}
        >
          {tagDisplay ? (
            <span>
              <span
                className="cd-game-tag-dot"
                style={{ background: tagDisplay.color }}
              />
              {tagDisplay.label}
            </span>
          ) : (
            AppStrings.CASINO_CONFIG_GAMES_TAG_ADD
          )}
        </button>
        <button
          type="button"
          className="cd-game-provider-link"
          onClick={() => setDrillOpen(true)}
          aria-label={formatString(
            AppStrings.CASINO_CONFIG_COLLECTIONS_PROVIDER_GAMES_ARIA,
            { name: provider.name },
          )}
        >
          {AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_GAMES}{" "}
          <RightOutlined style={{ fontSize: 10 }} />
        </button>
        <button
          type="button"
          className="cd-game-remove"
          onClick={handleRemove}
          disabled={removing}
          aria-label={formatString(
            AppStrings.CASINO_CONFIG_COLLECTIONS_REMOVE_ROW_ARIA,
            { name: provider.name },
          )}
        >
          <CloseOutlined />
        </button>
      </div>
      <TagModal
        open={tagModalOpen}
        gameLabel={provider.provider_id}
        currentTags={currentTagIds}
        availableTags={adminTags}
        loading={false}
        onSetTag={handleSetTag}
        onRemoveTag={handleRemoveTag}
        onClose={() => setTagModalOpen(false)}
      />
      <ProviderItemsModal
        open={drillOpen}
        collectionCode={code}
        providerId={provider.provider_id}
        providerName={provider.name}
        onClose={() => setDrillOpen(false)}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Provider Items drill-down modal                                    */
/* ------------------------------------------------------------------ */
function ProviderItemsModal({
  open,
  collectionCode,
  providerId,
  providerName,
  onClose,
}: {
  open: boolean;
  collectionCode: string;
  providerId: string;
  providerName: string;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setDebouncedSearch("");
    }
  }, [open]);

  const { data, isLoading } = useProviderItems(
    open ? collectionCode : null,
    open ? providerId : null,
    debouncedSearch || undefined,
  );

  const { mutateAsync: moveItem } = useMoveProviderItem(collectionCode, providerId);

  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const handleDrop = async (dropIdx: number) => {
    const items = data?.items ?? [];
    const item = dragIdx !== null && dragIdx !== dropIdx ? items[dragIdx] : null;
    const target = items[dropIdx];
    setDragIdx(null);
    setDragOverIdx(null);
    if (!item || !target) return;
    try {
      await moveItem({
        game_id: item.game_id,
        game_provider: item.game_provider,
        target_position: toMoveTargetPosition({
          target,
          dropIdx,
          movedIsPinned: Boolean(item.is_pinned),
          items,
        }),
      });
    } catch (err: unknown) {
      message.error((err as Error)?.message || AppStrings.CASINO_CONFIG_COLLECTIONS_DRILL_FAILED_MOVE);
    }
  };

  const handlePin = async (item: ProviderItem) => {
    const isPinned = Boolean(item.is_pinned);
    try {
      await moveItem({
        game_id: item.game_id,
        game_provider: item.game_provider,
        target_position: isPinned ? UNPIN_TO_TOP : PIN_TO_TOP,
      });
    } catch (err: unknown) {
      message.error((err as Error)?.message || AppStrings.CASINO_CONFIG_COLLECTIONS_DRILL_FAILED_MOVE);
    }
  };

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      footer={
        <div className="cd-add-footer">
          <button type="button" className="cd-add-cancel-btn" onClick={onClose}>
            {AppStrings.CASINO_CONFIG_COLLECTIONS_DRILL_CLOSE}
          </button>
        </div>
      }
      width={640}
      className="cd-add-modal"
      destroyOnHidden
      title={
        <div>
          <div className="cd-add-title">
            {providerName}{" "}
            <span className="cd-drill-provider-id">{providerId}</span>{" "}
            <span className="cd-drill-badge">{AppStrings.CASINO_CONFIG_COLLECTIONS_DRILL_BADGE}</span>
          </div>
          <div className="cd-add-selected">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_DRILL_DESCRIPTION} {providerId} {AppStrings.CASINO_CONFIG_COLLECTIONS_DRILL_DESCRIPTION_SUFFIX}
          </div>
        </div>
      }
    >
      <div className="cd-drill-toolbar">
        <Input
          prefix={<SearchOutlined />}
          placeholder={AppStrings.CASINO_CONFIG_COLLECTIONS_DRILL_SEARCH}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          className="cd-add-search cd-drill-search"
        />
        <span className="cd-games-total">{data?.total ?? 0} {AppStrings.CASINO_CONFIG_COLLECTIONS_DRILL_GAMES}</span>
      </div>
      {data && data.total > (data.items?.length ?? 0) && (
        <div className="cd-truncation-warning">
          {formatString(AppStrings.CASINO_CONFIG_COLLECTIONS_DRILL_TRUNCATION, {
            shown: String(data.items.length),
            total: String(data.total),
          })}
        </div>
      )}
      <div className="cd-add-list">
        {isLoading ? (
          <div className="cd-games-loader">
            <RebetLoader height="48px" width="48px" />
          </div>
        ) : (data?.items ?? []).length === 0 ? (
          <Empty description={AppStrings.CASINO_CONFIG_COLLECTIONS_DRILL_NO_GAMES} />
        ) : (
          (data?.items ?? []).map((item, idx) => {
            const isPinned = Boolean(item.is_pinned);
            return (
              <div
                key={`${item.game_provider}_${item.game_id}`}
                className={`cd-game-row ${dragOverIdx === idx ? "cd-game-row--drag-over" : ""}`}
                data-testid={`drill-row-${idx}`}
                draggable
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                onDrop={() => handleDrop(idx)}
                onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
              >
                <img src={ClipPathIcon} alt="" className="cd-game-drag-icon" />
                <span className="cd-game-idx" data-testid={`drill-idx-${item.game_id}`}>
                  {toVisualListPosition(idx)}
                </span>
                <div className="cd-game-label">
                  <span className="cd-game-name">{item.name}</span>
                  <span
                    className="cd-game-provider"
                    title={`${item.game_provider}/${item.game_id}`}
                  >
                    {item.game_provider}/{item.game_id}
                  </span>
                </div>
                {isPinned && (
                  <span className="cd-game-pinned-badge">
                  {AppStrings.CASINO_CONFIG_COLLECTIONS_BADGE_PINNED}
                </span>
                )}
                <span
                  className={`cd-game-status ${
                    item.is_active
                      ? "cd-game-status--active"
                      : "cd-game-status--inactive"
                  }`}
                >
                  {item.is_active ? AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_ACTIVE : AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_INACTIVE}
                </span>
                <button
                  type="button"
                  className={`cd-game-pin ${isPinned ? "cd-game-pin--active" : ""}`}
                  onClick={() => handlePin(item)}
                  aria-label={formatString(
                    isPinned
                      ? AppStrings.CASINO_CONFIG_COLLECTIONS_UNPIN_ARIA
                      : AppStrings.CASINO_CONFIG_COLLECTIONS_PIN_ARIA,
                    { name: item.name },
                  )}
                >
                  {isPinned ? (
                    <img
                      src={unpinIcon}
                      alt={AppStrings.CASINO_CONFIG_COLLECTIONS_UNPIN_ICON_ALT}
                      className="cd-pin-img"
                    />
                  ) : (
                    <PushpinOutlined />
                  )}
                </button>
              </div>
            );
          })
        )}
      </div>
    </AntModal>
  );
}

/* ------------------------------------------------------------------ */
/*  Providers tab (for kind: "providers" collections)                  */
/* ------------------------------------------------------------------ */
function ProvidersTab({
  code,
  collectionName,
}: {
  code: string;
  /** Rendered inside the modal title copy; a node so the canonical name can ride along as a tooltip. */
  collectionName: React.ReactNode;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [addProvidersOpen, setAddProvidersOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    data: providersData,
    isLoading,
    error,
  } = useCollectionProviders(code, "popular", debouncedSearch || undefined);

  const { mutateAsync: moveProvider } = useMoveCollectionProvider(code);

  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };
  const handleDragCancel = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleDrop = async (dropIdx: number) => {
    const items = providersData?.items ?? [];
    const prov = dragIdx !== null && dragIdx !== dropIdx ? items[dragIdx] : null;
    const target = items[dropIdx];
    setDragIdx(null);
    setDragOverIdx(null);
    if (!prov || !target) return;
    try {
      await moveProvider({
        provider_id: prov.provider_id,
        target_position: toMoveTargetPosition({
          target,
          dropIdx,
          movedIsPinned: Boolean(prov.is_pinned),
          items,
        }),
      });
    } catch (err: unknown) {
      message.error(
        (err as Error)?.message ||
          AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_MOVE_PROVIDER,
      );
    }
  };

  return (
    <div className="cd-games">
      <div className="cd-games-toolbar">
        <Input
          prefix={<SearchOutlined />}
          placeholder={AppStrings.CASINO_CONFIG_COLLECTIONS_SEARCH_PROVIDERS}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          className="cd-games-search"
        />
        <span className="cd-games-total">
          {providersData?.total ?? 0}{" "}
          {AppStrings.CASINO_CONFIG_COLLECTIONS_TOTAL_SUFFIX}
        </span>
        <Button
          intent="primary"
          className="cd-add-games-btn"
          onClick={() => setAddProvidersOpen(true)}
        >
          {AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_PROVIDERS}
        </Button>
      </div>

      <AddProvidersModal
        open={addProvidersOpen}
        code={code}
        collectionName={collectionName}
        onClose={() => setAddProvidersOpen(false)}
      />

      <div className="cd-games-list">
        {isLoading ? (
          <div className="cd-games-loader">
            <RebetLoader height="48px" width="48px" />
          </div>
        ) : error ? (
          <Empty description={AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_LOAD_PROVIDERS} />
        ) : (providersData?.items ?? []).length === 0 ? (
          <Empty description={AppStrings.CASINO_CONFIG_COLLECTIONS_NO_PROVIDERS} />
        ) : (
          (providersData?.items ?? []).map((prov, idx) => (
            <ProviderRow
              key={prov.provider_id}
              provider={prov}
              idx={idx}
              code={code}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragCancel={handleDragCancel}
              dragOverIdx={dragOverIdx}
            />
          ))
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Nest Existing modal                                                */
/* ------------------------------------------------------------------ */
function NestExistingModal({
  open,
  parentCode,
  parentName,
  kind,
  existingChildCodes,
  onClose,
}: {
  open: boolean;
  parentCode: string;
  parentName: string;
  kind: string;
  existingChildCodes: Set<string>;
  onClose: () => void;
}) {
  const { getName } = useCurationDisplayName();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setDebouncedSearch("");
      setSelected(new Set());
    }
  }, [open]);

  const { data, isLoading } = useAvailableCollections(
    open ? parentCode : null,
    kind,
    debouncedSearch || undefined,
  );

  const filteredItems = useMemo(
    () => (data?.items ?? []).filter((col) => !existingChildCodes.has(col.code)),
    [data, existingChildCodes],
  );

  const { mutateAsync: nestCollection, isPending: nesting } =
    useNestCollection(parentCode);

  const toggleSelect = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleLink = async () => {
    const codes = Array.from(selected);
    let successCount = 0;
    const errors: string[] = [];
    for (let i = 0; i < codes.length; i++) {
      try {
        await nestCollection(codes[i]);
        successCount++;
      } catch {
        errors.push(codes[i]);
      }
    }
    if (successCount > 0) {
      message.success(
        successCount === 1
          ? AppStrings.CASINO_CONFIG_COLLECTIONS_NEST_SUCCESS_SINGULAR
          : formatString(
              AppStrings.CASINO_CONFIG_COLLECTIONS_NEST_SUCCESS_PLURAL,
              { count: String(successCount) },
            ),
      );
    }
    if (errors.length > 0) {
      message.error(
        formatString(AppStrings.CASINO_CONFIG_COLLECTIONS_NEST_FAILED, {
          codes: errors.join(", "),
        }),
      );
    } else {
      onClose();
    }
  };

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      footer={
        <div className="cd-add-footer">
          <button type="button" className="cd-add-cancel-btn" onClick={onClose}>
            {AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_CANCEL}
          </button>
          <button
            type="button"
            className="cd-add-submit-btn"
            onClick={handleLink}
            disabled={selected.size === 0 || nesting}
          >
            {formatString(AppStrings.CASINO_CONFIG_COLLECTIONS_NEST_LINK_BUTTON, {
              count: String(selected.size),
            })}
          </button>
        </div>
      }
      width={640}
      className="cd-add-modal"
      destroyOnHidden
      title={
        <div>
          <div className="cd-add-title">{AppStrings.CASINO_CONFIG_COLLECTIONS_NEST_TITLE} {parentName}</div>
          <div className="cd-add-selected">
            {formatString(AppStrings.CASINO_CONFIG_GAMES_BULK_SELECTED, {
              count: String(selected.size),
            })}
          </div>
        </div>
      }
    >
      <Input
        prefix={<SearchOutlined />}
        placeholder={AppStrings.CASINO_CONFIG_COLLECTIONS_NEST_SEARCH}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        className="cd-add-search"
      />
      <div className="cd-add-list">
        {isLoading ? (
          <div className="cd-games-loader">
            <RebetLoader height="48px" width="48px" />
          </div>
        ) : filteredItems.length === 0 ? (
          <Empty description={AppStrings.CASINO_CONFIG_COLLECTIONS_NEST_NO_RESULTS} />
        ) : (
          filteredItems.map((col) => {
            const checked = selected.has(col.code);
            return (
              <div
                key={col.code}
                className={`cd-add-row ${checked ? "cd-add-row--selected" : ""}`}
                onClick={() => toggleSelect(col.code)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleSelect(col.code);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <CurationEntityName
                  className="cd-add-game-name"
                  entity={col}
                  displayName={getName(col)}
                />
                <span className="cd-add-game-id">{col.code}</span>
                <span className="cd-sub-nest-count">
                  {col.member_count ?? 0} {AppStrings.CASINO_CONFIG_COLLECTIONS_GAMES_SUFFIX}
                </span>
                <Checkbox
                  checked={checked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(col.code)}
                  className="cd-add-checkbox"
                />
              </div>
            );
          })
        )}
      </div>
    </AntModal>
  );
}

/* ------------------------------------------------------------------ */
/*  Subcollections tab                                                 */
/* ------------------------------------------------------------------ */
function SubcollectionsTab({
  code,
  name,
  kind,
  onOpenCollection,
}: {
  code: string;
  name: string;
  kind: string;
  onOpenCollection: (code: string) => void;
}) {
  const { data, isLoading } = useAdminCollections({ parentCode: code });
  const { getName } = useCurationDisplayName();
  const { mutateAsync: unnestCollection } = useUnnestCollection();
  const [unnesting, setUnnesting] = useState<string | null>(null);
  const [confirmingUnnest, setConfirmingUnnest] = useState<string | null>(null);
  const [nestModalOpen, setNestModalOpen] = useState(false);

  const children = data?.items ?? [];

  const handleConfirmUnnest = async (child: AdminCollection) => {
    setUnnesting(child.code);
    try {
      await unnestCollection(child.code);
      message.success(
        formatString(AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_UNNESTED, {
          name: child.name,
        }),
      );
      setConfirmingUnnest(null);
    } catch (err: unknown) {
      message.error((err as Error)?.message || AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_FAILED_UNNEST);
    } finally {
      setUnnesting(null);
    }
  };

  return (
    <div className="cd-sub-tab">
      <div className="cd-sub-header">
        <span className="cd-sub-description">
          {AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_NEST_DESCRIPTION}{" "}
          <b>{getCollectionKindLabel(kind)}</b>{" "}
          {AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_NEST_DESCRIPTION_SUFFIX}
        </span>
        <Button
          intent="primary"
          className="cd-sub-nest-existing-btn"
          onClick={() => setNestModalOpen(true)}
        >
          <LinkOutlined /> {AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_NEST_EXISTING}
        </Button>
      </div>

      <div className="cd-sub-list">
        {isLoading ? (
          <div className="cd-games-loader">
            <RebetLoader height="48px" width="48px" />
          </div>
        ) : children.length === 0 ? (
          <Empty description={AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_NO_SUBCOLLECTIONS} />
        ) : (
          children.map((child) => (
            <div key={child.code}>
              <div className="cd-sub-row">
                {child.icon_url && (
                  <img className="cd-sub-icon" src={child.icon_url} alt="" />
                )}
                <div className="cd-sub-info">
                  <CurationEntityName
                    className="cd-sub-name"
                    entity={child}
                    displayName={getName(child)}
                  />
                  <span className="cd-sub-code">{child.code}</span>
                </div>
                <span className={`collections-type-badge collections-type-badge--${child.kind}`}>
                  {getCollectionKindLabel(child.kind)}
                </span>
                <span className="cd-sub-count">
                  {child.child_count ?? 0} {AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_CHILDREN}
                </span>
                <span className="cd-sub-count">
                  {child.member_count ?? 0} {AppStrings.CASINO_CONFIG_COLLECTIONS_GAMES_SUFFIX}
                </span>
                <span className={`cd-sub-status ${child.is_active ? "cd-sub-status--active" : "cd-sub-status--inactive"}`}>
                  {child.is_active ? AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_ACTIVE : AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_INACTIVE}
                </span>
                <button
                  type="button"
                  className="cd-sub-unnest-btn"
                  onClick={() => setConfirmingUnnest(child.code)}
                  disabled={unnesting === child.code}
                >
                  <DisconnectOutlined /> {AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_UNNEST}
                </button>
                <button
                  type="button"
                  className="cd-sub-open-btn"
                  onClick={() => onOpenCollection(child.code)}
                >
                  {AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_OPEN} <RightOutlined style={{ fontSize: 10 }} />
                </button>
              </div>
              {confirmingUnnest === child.code && (
                <div className="cd-sub-unnest-confirm">
                  <span>
                    {AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_UNNEST_CONFIRM_PREFIX} <b>{child.code}</b>{AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_UNNEST_CONFIRM_SUFFIX}
                  </span>
                  <button
                    type="button"
                    className="cd-sub-unnest-cancel"
                    onClick={() => setConfirmingUnnest(null)}
                  >
                    {AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_UNNEST_CANCEL}
                  </button>
                  <button
                    type="button"
                    className="cd-sub-unnest-confirm-btn"
                    onClick={() => handleConfirmUnnest(child)}
                    disabled={unnesting === child.code}
                  >
                    <DisconnectOutlined /> {AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_UNNEST_CONFIRM}
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <NestExistingModal
        open={nestModalOpen}
        parentCode={code}
        parentName={name}
        kind={kind}
        existingChildCodes={new Set(children.map((c) => c.code))}
        onClose={() => setNestModalOpen(false)}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Add Games modal                                                    */
/* ------------------------------------------------------------------ */
const ADD_GAMES_PAGE_SIZE = 50;

function AddGamesModal({
  open,
  code,
  onClose,
}: {
  open: boolean;
  code: string;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 when search changes to avoid stale page outside new result set.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setSearch("");
      setDebouncedSearch("");
      setPage(1);
    }
  }, [open]);

  const { data, isLoading } = useAvailableGames(
    open ? code : null,
    debouncedSearch || undefined,
    page,
    ADD_GAMES_PAGE_SIZE,
  );

  const totalCount = data?.total ?? 0;
  // Base pagination on total only to keep controls steady while page fetches.
  const showPagination = totalCount > ADD_GAMES_PAGE_SIZE;

  const { mutateAsync: addGames, isPending: adding } = useAddCollectionGames(code);

  const toggleSelect = (game: AvailableGame) => {
    const key = `${game.provider}::${game.game_id}`;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleAdd = async () => {
    const games = Array.from(selected).map((key) => {
      const sepIdx = key.indexOf("::");
      const provider = key.slice(0, sepIdx);
      const game_id = key.slice(sepIdx + 2);
      return { provider, game_id };
    });
    try {
      await addGames(games);
      message.success(
        formatString(AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_SUCCESS, {
          count: String(games.length),
          unit:
            games.length > 1
              ? AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_GAMES
              : AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_GAME,
        }),
      );
      onClose();
    } catch (err: unknown) {
      message.error(
        (err as Error)?.message ||
          AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_FAILED,
      );
    }
  };

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      title={
        <div>
          <div className="cd-add-title">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_TITLE}{" "}
            &lsquo;{code}&rsquo;{" "}
            {AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_COLLECTION}
          </div>
          <div className="cd-add-selected">
            {formatString(AppStrings.CASINO_CONFIG_GAMES_BULK_SELECTED, {
              count: String(selected.size),
            })}
          </div>
        </div>
      }
      footer={
        <div className="cd-add-footer">
          <button type="button" className="cd-add-cancel-btn" onClick={onClose}>
            {AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_CANCEL}
          </button>
          <button
            type="button"
            className="cd-add-submit-btn"
            onClick={handleAdd}
            disabled={selected.size === 0 || adding}
          >
            {formatString(AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_PROVIDERS_SUBMIT, {
              count: String(selected.size),
              unit:
                selected.size === 1
                  ? AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_GAME
                  : AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_GAMES,
            })}
          </button>
        </div>
      }
      width={640}
      className="cd-add-modal"
      destroyOnHidden
    >
      <Input
        prefix={<SearchOutlined />}
        placeholder={AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_SEARCH}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        className="cd-add-search"
      />
      <div className="cd-add-list">
        {isLoading ? (
          <div className="cd-games-loader">
            <RebetLoader height="48px" width="48px" />
          </div>
        ) : (data?.items ?? []).length === 0 ? (
          <Empty description={AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_NO_RESULTS} />
        ) : (
          (data?.items ?? []).map((game) => {
            const key = `${game.provider}::${game.game_id}`;
            const checked = selected.has(key);
            return (
              <div
                key={key}
                className={`cd-add-row ${checked ? "cd-add-row--selected" : ""}`}
                onClick={() => toggleSelect(game)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleSelect(game);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className="cd-add-game-label">
                  <span className="cd-add-game-name">{game.name}</span>
                  <span
                    className="cd-add-game-id"
                    title={`${game.provider}/${game.game_id}`}
                  >
                    {game.provider}/{game.game_id}
                  </span>
                </div>
                <span
                  className={`cd-game-status ${
                    game.is_active
                      ? "cd-game-status--active"
                      : "cd-game-status--inactive"
                  }`}
                >
                  {game.is_active ? AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_ACTIVE : AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_INACTIVE}
                </span>
                <Checkbox
                  checked={checked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(game)}
                  className="cd-add-checkbox"
                />
              </div>
            );
          })
        )}
      </div>
      {showPagination && (
        <MinimalPagination
          total={totalCount}
          pageSize={ADD_GAMES_PAGE_SIZE}
          currentPage={page}
          onPageChange={setPage}
          itemLabel={AppStrings.CASINO_CONFIG_COLLECTIONS_GAMES_SUFFIX}
        />
      )}
    </AntModal>
  );
}

/* ------------------------------------------------------------------ */
/*  Add Providers modal                                                */
/* ------------------------------------------------------------------ */
const ADD_PROVIDERS_PAGE_SIZE = 20;

function AddProvidersModal({
  open,
  code,
  collectionName,
  onClose,
}: {
  open: boolean;
  code: string;
  /** Rendered inside the modal title copy; a node so the canonical name can ride along as a tooltip. */
  collectionName: React.ReactNode;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setSearch("");
      setDebouncedSearch("");
      setPage(1);
    }
  }, [open]);

  const { data, isLoading } = useAvailableProviders(
    open ? code : null,
    debouncedSearch || undefined,
    page,
    ADD_PROVIDERS_PAGE_SIZE,
  );

  const totalCount = data?.total ?? 0;
  const itemCount = data?.items?.length ?? 0;
  const totalPages =
    data?.meta?.total_pages ??
    Math.max(1, Math.ceil(totalCount / ADD_PROVIDERS_PAGE_SIZE));
  const showPagination =
    !isLoading &&
    totalCount > 0 &&
    (totalPages > 1 ||
      Boolean(data?.meta?.has_next || data?.meta?.has_prev) ||
      totalCount > itemCount);

  const { mutateAsync: addProviders, isPending: adding } =
    useAddCollectionProviders(code);

  const toggleSelect = (provider: AvailableProvider) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(provider.provider_id)) next.delete(provider.provider_id);
      else next.add(provider.provider_id);
      return next;
    });
  };

  const handleAdd = async () => {
    const providerIds = Array.from(selected);
    try {
      await addProviders(providerIds);
      message.success(
        providerIds.length === 1
          ? AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_PROVIDERS_SUCCESS_SINGULAR
          : formatString(
              AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_PROVIDERS_SUCCESS_PLURAL,
              { count: String(providerIds.length) },
            ),
      );
      onClose();
    } catch (err: unknown) {
      message.error(
        (err as Error)?.message ||
          AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_PROVIDERS_FAILED,
      );
    }
  };

  const submitLabel = formatString(
    AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_PROVIDERS_SUBMIT,
    {
      count: String(selected.size),
      unit:
        selected.size === 1
          ? AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_PROVIDERS_PROVIDER
          : AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_PROVIDERS_PROVIDERS,
    },
  );

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      title={
        <div>
          <div className="cd-add-title">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_PROVIDERS_TITLE}{" "}
            &lsquo;{collectionName}&rsquo;{" "}
            {AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES_COLLECTION}
          </div>
          <div className="cd-add-selected">
            {formatString(AppStrings.CASINO_CONFIG_GAMES_BULK_SELECTED, {
              count: String(selected.size),
            })}
          </div>
        </div>
      }
      footer={
        <div className="cd-add-footer">
          <button type="button" className="cd-add-cancel-btn" onClick={onClose}>
            {AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_PROVIDERS_CANCEL}
          </button>
          <button
            type="button"
            className="cd-add-submit-btn"
            onClick={handleAdd}
            disabled={selected.size === 0 || adding}
          >
            {submitLabel}
          </button>
        </div>
      }
      width={640}
      className="cd-add-modal"
      destroyOnHidden
    >
      <Input
        prefix={<SearchOutlined />}
        placeholder={AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_PROVIDERS_SEARCH}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        className="cd-add-search"
      />
      <div className="cd-add-list">
        {isLoading ? (
          <div className="cd-games-loader">
            <RebetLoader height="48px" width="48px" />
          </div>
        ) : (data?.items ?? []).length === 0 ? (
          <Empty
            description={
              AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_PROVIDERS_NO_RESULTS
            }
          />
        ) : (
          (data?.items ?? []).map((provider) => {
            const checked = selected.has(provider.provider_id);
            const slug =
              provider.slug || provider.identifier || provider.provider_id;
            return (
              <div
                key={provider.provider_id}
                className={`cd-add-row ${checked ? "cd-add-row--selected" : ""}`}
                onClick={() => toggleSelect(provider)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleSelect(provider);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <span className="cd-add-game-name">{provider.name}</span>
                <span className="cd-add-game-id">{slug}</span>
                <span
                  className={`cd-game-status ${
                    provider.is_active
                      ? "cd-game-status--active"
                      : "cd-game-status--inactive"
                  }`}
                >
                  {provider.is_active
                    ? AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_ACTIVE
                    : AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_INACTIVE}
                </span>
                <Checkbox
                  checked={checked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(provider)}
                  className="cd-add-checkbox"
                />
              </div>
            );
          })
        )}
      </div>
      {showPagination && data && (
        <MinimalPagination
          total={totalCount}
          pageSize={ADD_PROVIDERS_PAGE_SIZE}
          currentPage={page}
          onPageChange={setPage}
          itemLabel={AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_PROVIDERS_PROVIDERS.toLowerCase()}
        />
      )}
    </AntModal>
  );
}

/* ------------------------------------------------------------------ */
/*  Games tab                                                         */
/* ------------------------------------------------------------------ */

function GamesTab({
  code,
  defaultView,
}: {
  code: string;
  defaultView: string;
}) {
  const initialView =
    defaultView === "all"
      ? "all"
      : defaultView === "provider"
        ? "provider"
        : "popular";

  const [view, setView] = useState<"all" | "popular" | "provider">(
    initialView as "all" | "popular" | "provider",
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [addGamesOpen, setAddGamesOpen] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, view]);

  const { data: allData, isLoading: allLoading, error: allError } = useCollectionGames(
    code,
    "all",
    view === "all" && debouncedSearch ? debouncedSearch : undefined,
    view === "all" ? page : 1,
    GAMES_TAB_PAGE_SIZE,
  );
  const { data: popularData, isLoading: popularLoading, error: popularError } = useCollectionGames(
    code,
    "popular",
    view === "popular" && debouncedSearch ? debouncedSearch : undefined,
    view === "popular" ? page : 1,
    GAMES_TAB_PAGE_SIZE,
  );

  const activeData = view === "popular" ? popularData : allData;
  const isLoading = view === "popular" ? popularLoading : allLoading;
  const activeError = view === "popular" ? popularError : allError;
  const totalCount = activeData?.total ?? 0;
  const showPagination = view !== "provider" && totalCount > GAMES_TAB_PAGE_SIZE;

  const popularGameKeys = useMemo(() => {
    const keys = new Set<string>();
    (popularData?.items ?? []).forEach((g) =>
      keys.add(`${g.provider}::${g.game_id}`),
    );
    return keys;
  }, [popularData]);

  const { mutateAsync: moveGame } = useMoveCollectionGame(code);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const handleDragStart = (idx: number) => {
    setDragIdx(idx);
  };
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };
  const handleDragCancel = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleDrop = async (dropIdx: number) => {
    const items = activeData?.items ?? [];
    const pageOffset = (page - 1) * GAMES_TAB_PAGE_SIZE;
    const localDragIdx = dragIdx !== null ? dragIdx - pageOffset : null;
    const localDropIdx = dropIdx - pageOffset;
    const game =
      localDragIdx !== null && dragIdx !== dropIdx ? items[localDragIdx] : null;
    const target = items[localDropIdx];
    setDragIdx(null);
    setDragOverIdx(null);
    if (!game || !target) return;
    try {
      await moveGame({
        view: view === "popular" ? "popular" : "all",
        provider: game.provider,
        game_id: game.game_id,
        target_position: toMoveTargetPosition({
          target,
          dropIdx: localDropIdx,
          movedIsPinned: Boolean(game.is_pinned),
          items,
        }),
      });
    } catch (err: unknown) {
      message.error((err as Error)?.message || AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_MOVE_GAME);
    }
  };

  return (
    <div className="cd-games">
      <div className="cd-games-views">
        <button
          type="button"
          className={`cd-view-btn ${view === "all" ? "cd-view-btn--active" : ""}`}
          onClick={() => setView("all")}
        >
          {AppStrings.CASINO_CONFIG_COLLECTIONS_FULL_LIST}
        </button>
        <button
          type="button"
          className={`cd-view-btn ${view === "popular" ? "cd-view-btn--active" : ""}`}
          onClick={() => setView("popular")}
        >
          {AppStrings.CASINO_CONFIG_COLLECTIONS_POPULAR}
        </button>
        <button
          type="button"
          className={`cd-view-btn ${view === "provider" ? "cd-view-btn--active" : ""}`}
          onClick={() => setView("provider")}
        >
          {AppStrings.CASINO_CONFIG_COLLECTIONS_GROUPED_BY_PROVIDER}
        </button>
      </div>

      <div className="cd-games-toolbar">
        <Input
          prefix={<SearchOutlined />}
          placeholder={AppStrings.CASINO_CONFIG_COLLECTIONS_SEARCH_GAMES}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          className="cd-games-search"
        />
        <span className="cd-games-total">
          {activeData?.total ?? 0}{" "}
          {AppStrings.CASINO_CONFIG_COLLECTIONS_TOTAL_SUFFIX}
        </span>
        {view === "all" && (
          <Button
            intent="primary"
            className="cd-add-games-btn"
            onClick={() => setAddGamesOpen(true)}
          >
            {AppStrings.CASINO_CONFIG_COLLECTIONS_ADD_GAMES}
          </Button>
        )}
      </div>

      <div className="cd-games-list">
        {view === "provider" ? (
          <ProviderGroupView code={code} search={debouncedSearch} />
        ) : isLoading ? (
          <div className="cd-games-loader">
            <RebetLoader height="48px" width="48px" />
          </div>
        ) : activeError ? (
          <Empty description={AppStrings.CASINO_CONFIG_COLLECTIONS_FAILED_LOAD_GAMES} />
        ) : (activeData?.items ?? []).length === 0 ? (
          <Empty description={AppStrings.CASINO_CONFIG_COLLECTIONS_NO_GAMES} />
        ) : (
          (activeData?.items ?? []).map((game, idx) => (
            <GameRow
              key={`${game.provider}_${game.game_id}`}
              game={game}
              idx={(page - 1) * GAMES_TAB_PAGE_SIZE + idx}
              code={code}
              view={view === "popular" ? "popular" : "all"}
              showStar={view === "all"}
              popularGameKeys={popularGameKeys}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragCancel={handleDragCancel}
              dragOverIdx={dragOverIdx}
            />
          ))
        )}
      </div>
      {showPagination && (
        <MinimalPagination
          total={totalCount}
          pageSize={GAMES_TAB_PAGE_SIZE}
          currentPage={page}
          onPageChange={setPage}
          itemLabel={AppStrings.CASINO_CONFIG_COLLECTIONS_GAMES_SUFFIX}
        />
      )}
      <AddGamesModal
        open={addGamesOpen}
        code={code}
        onClose={() => setAddGamesOpen(false)}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main modal                                                        */
/* ------------------------------------------------------------------ */
function CollectionDetailModal({
  open,
  collectionCode,
  onClose,
  onNavigate,
  canUploadImages = true,
}: Props) {
  const isDevMode = useIsDevMode();
  const { getName } = useCurationDisplayName();
  const { data: detail, isLoading } = useCollectionDetail(
    open ? collectionCode : null,
  );

  const tabItems: Array<{ key: string; label: React.ReactNode; children: React.ReactNode }> = useMemo(() => {
    if (!detail) return [];
    const items: Array<{ key: string; label: React.ReactNode; children: React.ReactNode }> = [
      {
        key: "general",
        label: AppStrings.CASINO_CONFIG_COLLECTIONS_GENERAL_TAB,
        children: (
          <GeneralTab
            detail={detail}
            onSaved={onClose}
            canUploadImages={canUploadImages}
          />
        ),
      },
    ];
    // Placement-only collections (e.g. Big Winners) expose name/icon on
    // General; members are computed on Features and must not be edited here.
    const canManageMembers = !isSpecialCollection(detail.code);
    if (canManageMembers && detail.kind === "providers") {
      items.push({
        key: "providers",
        label: (
          <span>
            {AppStrings.CASINO_CONFIG_COLLECTIONS_PROVIDERS_TAB}{" "}
            <span className="cd-tab-count">{detail.member_count ?? 0}</span>
          </span>
        ),
        children: (
          <ProvidersTab
            code={detail.code}
            collectionName={
              <CurationEntityName entity={detail} displayName={getName(detail)} />
            }
          />
        ),
      });
    } else if (canManageMembers && detail.kind === "games") {
      items.push({
        key: "games",
        label: (
          <span>
            {AppStrings.CASINO_CONFIG_COLLECTIONS_GAMES_TAB}{" "}
            <span className="cd-tab-count">{detail.member_count ?? 0}</span>
          </span>
        ),
        children: (
          <GamesTab
            code={detail.code}
            defaultView={detail.default_view || "popular"}
          />
        ),
      });
    }
    if (canManageMembers && detail.kind === "games" && isDevMode) {
      items.push({
        key: "subcollections",
        label: (
          <span>
            {AppStrings.CASINO_CONFIG_COLLECTIONS_SUBCOLLECTIONS_TAB}{" "}
            <span className="cd-tab-count">{detail.child_count ?? 0}</span>
          </span>
        ),
        children: (
          <SubcollectionsTab
            code={detail.code}
            name={detail.name}
            kind={detail.kind}
            onOpenCollection={(childCode) => {
              if (onNavigate) onNavigate(childCode);
            }}
          />
        ),
      });
    }
    return items;
  }, [detail, onNavigate, onClose, isDevMode, canUploadImages, getName]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      footer={null}
      width={680}
      className="cd-modal"
      rootClassName="cd-modal-root"
      closeIcon={<CloseOutlined />}
      title={
        detail ? (
          <div className="cd-modal-header">
            {detail.icon_url && (
              <img className="cd-header-icon" src={detail.icon_url} alt="" />
            )}
            <div>
              <div className="cd-header-code">{detail.code}</div>
              <div className="cd-header-name">
                <b>
                  <CurationEntityName
                    entity={detail}
                    displayName={getName(detail)}
                  />
                </b>
                <span className="cd-header-badge cd-header-badge--kind">
                  {detail.kind === "providers" ? AppStrings.CASINO_CONFIG_COLLECTIONS_PROVIDERS_COLLECTION : AppStrings.CASINO_CONFIG_COLLECTIONS_GAMES_COLLECTION}
                </span>
                <span
                  className={`cd-header-badge ${
                    detail.is_active
                      ? "cd-header-badge--active"
                      : "cd-header-badge--inactive"
                  }`}
                >
                  {detail.is_active ? AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_ACTIVE : AppStrings.CASINO_CONFIG_COLLECTIONS_STATUS_INACTIVE}
                </span>
              </div>
            </div>
          </div>
        ) : null
      }
    >
      {isLoading ? (
        <div className="cd-loader">
          <RebetLoader height="64px" width="64px" />
        </div>
      ) : detail ? (
        <Tabs items={tabItems} className="cd-tabs" />
      ) : (
        <Empty description={AppStrings.CASINO_CONFIG_COLLECTIONS_NOT_FOUND} />
      )}
    </Modal>
  );
}

export default CollectionDetailModal;
